/* eslint-disable @typescript-eslint/no-explicit-any -- casts formulaire / handlers génériques */
import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaRegFile, FaSearch, FaTrashAlt } from 'react-icons/fa';
import type { TFunction } from 'i18next';
import U2000ActionBtn from '../../../../u2000Core/components/buttons/U2000ActionBtn';
import U2000DetailTable from '../../../../u2000Core/components/tables/detail/U2000DetailTable';
import { U2000IdCheckedBoxDisplay } from '../../../../u2000Core/components/tables/display/U2000IdCheckedBoxDisplay';
import { U2000IdHeaderCheckedBoxDisplay } from '../../../../u2000Core/components/tables/display/U2000IdHeaderCheckedBoxDisplay';
import U2000ResponsiveToolbar from '../../../../u2000Core/components/toolbar/U2000ResponsiveToolbar';
import { useU2000AuthContext } from '../../../../u2000Core/context/auth/U2000UseAuthContext';
import useU2000AxiosAuthenticated from '../../../../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError } from '../../../../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../../../../u2000Core/context/toast/U2000ToastContext';
import { useU2000GridStateHandlers } from '../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers';
import u2000FormState, { U2000FormStateEnumState, type U2000FormState } from '../../../../u2000Core/reducer/formReducer/U2000FormState';
import {
  u2000IntFormState,
  useU2000FormStateHandlers,
  type U2000FormStateHandlersReturn,
} from '../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import type { U2000TableColumn } from '../../../../u2000Core/components/tables/basic/U2000BasicTableList';
import { NS, U3202ROLES } from '../../../U3202';
import {
  u3202ActivatedModelToGridRow,
  u3202GridRowToActivatedModel,
  doDiscoverModels,
  luHandleAppiError,
  type U3202OrgModelConfigGridRow,
  type U3202OrgProviderConfigFormData,
} from '../../../U3202Service';
import {
  createEmptyU3202CatalogModelFormData,
  u3202ValidateCatalogModelForm,
  type U3202CatalogModelFormData,
} from '../../../utils/u3202CatalogModelFormUtils';
import {
  u3202MergeDiscoveredModels,
  u3202CanDiscoverModels,
  u3202BuildDiscoverModelsRequest,
} from '../../../utils/u3202DiscoverModelsUtils';
import { u3202FormatCapabilitiesSummaryText } from '../../../utils/u3202FormatCapabilitiesSummary';
import U3202CatalogModelDlg from './U3202CatalogModelDlg';
import U3202CatalogModelDeleteDlg from './U3202CatalogModelDeleteDlg';
import { u3202OrgModelConfigHandler } from './U3202OrgModelConfigHandlers';

export type U3202OrgProviderConfigFormExtendedHandlers = U2000FormStateHandlersReturn<U3202OrgProviderConfigFormData>;

interface U3202OrgModelConfigBodyProps {
  parentFormStateHandlers: U3202OrgProviderConfigFormExtendedHandlers;
}

function u3202FormatParameterSummary(row: U3202OrgModelConfigGridRow, t: TFunction): string {
  if (!row.active) {
    return '—';
  }
  const keys = row.parameterSchema ? Object.keys(row.parameterSchema) : Object.keys(row.parameterValues ?? {});
  if (keys.length === 0) {
    return t('lblNoParameters', { ns: NS });
  }
  return keys
    .slice(0, 3)
    .map((key) => `${key}: ${String(row.parameterValues[key] ?? '')}`)
    .join(', ');
}

interface U3202OrgModelConfigColumnsOptions {
  readModeRowClick?: (row: U3202OrgModelConfigGridRow) => void;
}

function getColumnsDef(t: TFunction, options?: U3202OrgModelConfigColumnsOptions): U2000TableColumn[] {
  return [
    {
      name: 'id',
      label: '',
      widthClass: 'w-[50px]',
      type: 'number',
      align: 'center',
      headerDisplay: (column, label, allRowSelected, handleSelectAllRow) => (
        <U2000IdHeaderCheckedBoxDisplay
          column={column}
          label={label}
          disabled={false}
          allRowSelected={allRowSelected}
          handleSelectAllRow={handleSelectAllRow}
        />
      ),
      display: (column, value, row, allowRowSelection, rowUpdated) => {
        const checkbox = (
          <U2000IdCheckedBoxDisplay
            column={column}
            value={value}
            row={row}
            disabled={false}
            rowUpdated={rowUpdated}
            allowRowSelection={allowRowSelection}
          />
        );
        if (options?.readModeRowClick) {
          return (
            <div
              className="cursor-pointer"
              role="presentation"
              onClick={(event) => {
                event.stopPropagation();
                options.readModeRowClick!(row as U3202OrgModelConfigGridRow);
              }}
            >
              {checkbox}
            </div>
          );
        }
        return checkbox;
      },
      isCriteriaField: false,
    },
    {
      name: 'modelCode',
      label: t('lblModel', { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      type: 'string',
      align: 'left',
      display: (_column, _value, row) => {
        const modelRow = row as U3202OrgModelConfigGridRow;
        return `${modelRow.modelName} (${modelRow.modelCode})`;
      },
    },
    {
      name: 'active',
      label: t('activatedModels.active', { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      type: 'boolean',
      align: 'center',
      display: (_column, value) => {
        if (value === true) return t('lblActiveYes', { ns: NS });
        if (value === false) return t('lblActiveNo', { ns: NS });
        return null;
      },
    },
    {
      name: 'capabilities',
      label: t('capabilities.columnLabel', { ns: NS }),
      inGridField: true,
      sortable: false,
      isDefaultColumn: true,
      type: 'string',
      align: 'left',
      display: (_column, _value, row) =>
        u3202FormatCapabilitiesSummaryText((row as U3202OrgModelConfigGridRow).capabilities, t, NS),
    },
    {
      name: 'parameterValues',
      label: t('sectionParameters', { ns: NS }),
      inGridField: true,
      sortable: false,
      isDefaultColumn: true,
      type: 'string',
      align: 'left',
      display: (_column, _value, row) => u3202FormatParameterSummary(row as U3202OrgModelConfigGridRow, t),
    },
  ];
}

const U3202OrgModelConfigBody: React.FC<U3202OrgModelConfigBodyProps> = ({
  parentFormStateHandlers,
}) => {
  const { t } = useTranslation();
  const { hasRole } = useU2000AuthContext();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const toast = useToast();
  const [discovering, setDiscovering] = useState(false);
  const [formState, formStateDispatch] = useReducer<U2000FormState, [action: { type: string; data?: unknown }]>(
    u2000FormState,
    u2000IntFormState,
  );
  const gridStateHandlers = useU2000GridStateHandlers<U3202OrgModelConfigGridRow>();

  const parentData = parentFormStateHandlers.getData();
  const parentModels = parentData?.models ?? [];
  const providerId = parentData?.providerId ?? '';

  const translateFormErrors = useCallback(
    (errors: Record<string, string>) => {
      const translated: Record<string, string> = {};
      for (const [key, code] of Object.entries(errors)) {
        translated[key] = t(code, { ns: NS, defaultValue: code });
      }
      return translated;
    },
    [t],
  );

  const catalogFormHandlers = useU2000FormStateHandlers<U3202CatalogModelFormData>({
    formState,
    formStateDispatch,
    businessHandlers: {
      newRecord: () => createEmptyU3202CatalogModelFormData(providerId),
      validateForm: (row) => translateFormErrors(u3202ValidateCatalogModelForm(row)),
    },
  });

  const isReadOnly = parentFormStateHandlers.getFormState() === U2000FormStateEnumState.READ;

  const canDiscover = !isReadOnly && u3202CanDiscoverModels(parentData);

  const handleDiscoverModels = useCallback(async () => {
    const parent = parentFormStateHandlers.getData();
    if (!parent || !u3202CanDiscoverModels(parent)) {
      return;
    }
    setDiscovering(true);
    try {
      const response = await doDiscoverModels(u2000AxiosAuthenticated, u3202BuildDiscoverModelsRequest(parent));
      const discovered = response.data?.result?.models ?? [];
      const merged = u3202MergeDiscoveredModels(parent.models ?? [], discovered);
      const updated = { ...parent, models: merged };
      parentFormStateHandlers.setData(updated);
      const validateForm = parentFormStateHandlers.getBusinessHandlers?.().validateForm;
      if (validateForm) {
        parentFormStateHandlers.setErrors(validateForm(updated));
      }
      toast.success(
        t('discoverModels.success', { ns: NS, count: discovered.length }),
        t('successTitle', { ns: NS }),
      );
    } catch (err) {
      const errorResult = U2000HandleApiError(err as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors.length > 0) {
        errorResult.genericErrors.forEach((e) => toast.error(e.text, e.title));
      } else {
        toast.error(t('discoverModels.failed', { ns: NS }), t('errorTitle', { ns: NS }));
      }
    } finally {
      setDiscovering(false);
    }
  }, [parentFormStateHandlers, t, toast, u2000AxiosAuthenticated]);

  const handler = u3202OrgModelConfigHandler({
    gridStateHandlers,
    t,
    providerId,
    getParentFormState: parentFormStateHandlers.getFormState,
    formStateHandlers: catalogFormHandlers,
  });
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    const columns = getColumnsDef(t, {
      readModeRowClick: isReadOnly
        ? (row) => handlerRef.current.onRowClick(row)
        : undefined,
    });
    gridStateHandlers.setColumns(columns);
    gridStateHandlers.setSelectedFields(columns.filter((c) => c.isDefaultColumn).map((c) => c.name));

    const selectedRows = gridStateHandlers.getSelectedRows();
    const selectedId =
      isReadOnly && selectedRows.length === 1 ? selectedRows[0]?.id : undefined;

    const list = parentModels.map((row) => {
      const gridRow = u3202ActivatedModelToGridRow(row);
      if (selectedId != null && String(gridRow.id) === String(selectedId)) {
        return { ...gridRow, idChecked: true };
      }
      return gridRow;
    });
    gridStateHandlers.setData(list);
    gridStateHandlers.setTotalRows(list.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resync volontaire sur parentModels / mode lecture
  }, [t, parentModels, isReadOnly]);

  useEffect(() => {
    if (handler.dataChangedEvent === 0) {
      return;
    }
    const parent = parentFormStateHandlers.getData();
    if (!parent) {
      return;
    }
    const models = handler.gridStateHandlers.getData().map((row) => u3202GridRowToActivatedModel(row));
    const updated = { ...parent, models };
    parentFormStateHandlers.setData(updated);
    // Re-valide le formulaire parent : rafraîchit l'état du bouton Enregistrer
    // (ex. l'exigence « au moins un modèle » se lève dès qu'une ligne est ajoutée).
    const validateForm = parentFormStateHandlers.getBusinessHandlers?.().validateForm;
    if (validateForm) {
      parentFormStateHandlers.setErrors(validateForm(updated));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync volontaire sur dataChangedEvent uniquement
  }, [handler.dataChangedEvent]);

  const disabledUpdateBtn = isReadOnly || gridStateHandlers.getSelectedRows().length !== 1;
  const disableDeleteBtn = isReadOnly || gridStateHandlers.getSelectedRows().length === 0;

  const actions = [
    {
      key: 'discover',
      label: t('btnDiscoverModels', { ns: NS }),
      handleClick: () => void handleDiscoverModels(),
      hasRole: hasRole(U3202ROLES.EDIT),
      disabled: !canDiscover || discovering,
      icon: FaSearch,
      Component: U2000ActionBtn,
    },
    {
      key: 'modify',
      label: t('btnModify', { ns: NS }),
      handleClick: handler.onModify,
      hasRole: hasRole(U3202ROLES.EDIT),
      disabled: disabledUpdateBtn,
      icon: FaRegFile,
      Component: U2000ActionBtn,
    },
    {
      key: 'delete',
      label: t('btnDelete', { ns: NS }),
      handleClick: handler.onDelete,
      hasRole: hasRole(U3202ROLES.DELETE),
      disabled: disableDeleteBtn,
      icon: FaTrashAlt,
      Component: U2000ActionBtn,
    },
  ];

  return (
    <>
      <U3202CatalogModelDlg handler={handler} t={t} NS={NS} readonly={isReadOnly} parentData={parentData} />
      <U3202CatalogModelDeleteDlg handler={handler} t={t} NS={NS} />
      <div className="mb-1 mt-1 flex items-center gap-x-2 border-b-2 pb-3">
        <U2000ResponsiveToolbar actions={actions} />
      </div>
      <U2000DetailTable
        gridStateHandlers={gridStateHandlers}
        allowRowSelection={!isReadOnly}
        rowClickFn={(row) => handler.onRowClick(row as U3202OrgModelConfigGridRow)}
        label={t('lblOrgModelConfigList', {
          count: gridStateHandlers.getTotalRows(),
          ns: NS,
        })}
      />
    </>
  );
};

export default U3202OrgModelConfigBody;
